import { css, html, nothing, type PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { keyed } from 'lit/directives/keyed.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { styleMap } from 'lit/directives/style-map.js'
import { filter, finalize, fromEvent, ignoreElements, map, merge, switchMap, take, takeUntil, tap } from 'rxjs'
import { POINTER_SESSION_THRESHOLD_PX } from '@schmancy/directives/pointer-session'
import { type SchmancyTone, toneColors } from '@schmancy/theme/tone'
import { HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import '@schmancy/primitives/typography/typography'

export type SchmancyRangeChangeEvent = CustomEvent<{ value: number; stop?: string }>

/** A named place on the rail, in the host's word. */
export interface SchmancyRangeStop {
	id: string
	word: string
}

const TICKS = 25
const TICK_PLACES = Array.from({ length: TICKS }, (_tick, tick) => tick / (TICKS - 1))

/**
 * @element schmancy-range
 * Range input (numeric slider).
 * @fires change - Fires on value change with `{ value: number }`.
 */
@customElement('schmancy-range')
export class SchmancyRange extends HostAriaLabelMixin(
	SchmancyFormField(css`
	input[type='range'] {
		-webkit-appearance: none;
		appearance: none;
		width: 100%;
		outline: none;
		cursor: pointer;
	}
	input[type='range']:disabled {
		opacity: 0.38;
		cursor: not-allowed;
	}
	:host input[type='range'] {
		height: 48px;
		margin: 0;
		background: transparent;
		touch-action: pan-y;
	}
	:host input[type='range']::-webkit-slider-runnable-track {
		height: 12px;
		translate: 0 var(--schmancy-range-visual-offset-y, var(--range-label-offset-y, 0px));
		border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
		background: linear-gradient(to right, currentColor 0%, currentColor var(--range-progress, 0%), color-mix(in srgb, currentColor 30%, transparent) var(--range-progress, 0%), color-mix(in srgb, currentColor 30%, transparent) 100%);
	}
	:host input[type='range']::-moz-range-track {
		height: 12px;
		translate: 0 var(--schmancy-range-visual-offset-y, var(--range-label-offset-y, 0px));
		border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
		background: linear-gradient(to right, currentColor 0%, currentColor var(--range-progress, 0%), color-mix(in srgb, currentColor 30%, transparent) var(--range-progress, 0%), color-mix(in srgb, currentColor 30%, transparent) 100%);
	}
	:host input[type='range']::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		box-sizing: border-box;
		width: 32px;
		height: 32px;
		border-radius: var(--schmancy-radius-circle, 50%);
		background: currentColor;
		cursor: pointer;
		margin-top: -10px;
		border: 4px solid var(--schmancy-sys-color-surface-default);
		box-shadow: 0 0 0 1px currentColor;
		transition: box-shadow 120ms ease;
	}
	:host input[type='range']::-moz-range-thumb {
		box-sizing: border-box;
		translate: 0 var(--schmancy-range-visual-offset-y, var(--range-label-offset-y, 0px));
		width: 32px;
		height: 32px;
		border-radius: var(--schmancy-radius-circle, 50%);
		background: currentColor;
		cursor: pointer;
		border: 4px solid var(--schmancy-sys-color-surface-default);
		box-shadow: 0 0 0 1px currentColor;
		transition: box-shadow 120ms ease;
	}
	@media (hover: hover) and (pointer: fine) {
		:host input[type='range']:not(:disabled):hover::-webkit-slider-thumb {
			box-shadow: 0 0 0 6px color-mix(in srgb, currentColor 18%, transparent);
		}
		:host input[type='range']:not(:disabled):hover::-moz-range-thumb {
			box-shadow: 0 0 0 6px color-mix(in srgb, currentColor 18%, transparent);
		}
	}
	:host input[type='range']:not(:disabled):focus-visible::-webkit-slider-thumb,
	:host input[type='range']:not(:disabled):active::-webkit-slider-thumb {
		box-shadow: 0 0 0 8px color-mix(in srgb, currentColor 24%, transparent);
	}
	:host input[type='range']:not(:disabled):focus-visible::-moz-range-thumb,
	:host input[type='range']:not(:disabled):active::-moz-range-thumb {
		box-shadow: 0 0 0 8px color-mix(in srgb, currentColor 24%, transparent);
	}
	:host input[type='range']:focus-visible::-webkit-slider-thumb {
		outline: 2px solid currentColor;
		outline-offset: 2px;
	}
	:host input[type='range']:focus-visible::-moz-range-thumb {
		outline: 2px solid currentColor;
		outline-offset: 2px;
	}
	@media (prefers-reduced-motion: reduce) {
		:host input[type='range']::-webkit-slider-thumb {
			transition: none;
		}
		:host input[type='range']::-moz-range-thumb {
			transition: none;
		}
	}
	/* The rising rail paints itself under a native range nobody sees, whose thumb is what a finger takes.
	   That thumb's centre runs the rail's whole width because it hangs half its own width past each end. */
	:host([variant='rising']) input[type='range'] {
		position: absolute;
		top: 0;
		left: -22px;
		z-index: 1;
		width: calc(100% + 44px);
		opacity: 0;
		cursor: grab;
	}
	:host([variant='rising']) input[type='range']:active {
		cursor: grabbing;
	}
	:host([variant='rising']) input[type='range']:disabled {
		cursor: not-allowed;
	}
	:host([variant='rising']) input[type='range']::-webkit-slider-runnable-track {
		height: 48px;
		translate: none;
		background: none;
	}
	:host([variant='rising']) input[type='range']::-moz-range-track {
		height: 48px;
		translate: none;
		background: none;
	}
	:host([variant='rising']) input[type='range']::-webkit-slider-thumb {
		width: 44px;
		height: 48px;
		margin-top: 0;
		border: 0;
		box-shadow: none;
	}
	:host([variant='rising']) input[type='range']::-moz-range-thumb {
		width: 44px;
		height: 48px;
		translate: none;
		border: 0;
		box-shadow: none;
		background: transparent;
	}
	[data-thumb] {
		left: calc(var(--at) * 100%);
		transition: left 300ms var(--schmancy-ease-spring-smooth, cubic-bezier(0.34, 1.56, 0.64, 1)), box-shadow 300ms ease, scale 150ms ease;
	}
	input:active ~ [data-thumb] {
		scale: 1.1;
	}
	input:focus-visible ~ [data-thumb] {
		outline: 2px solid currentColor;
		outline-offset: 2px;
	}
	[data-thumb][data-glow='some'] {
		box-shadow: 0 0 8px 0 color-mix(in srgb, currentColor 40%, transparent);
	}
	[data-thumb][data-glow='full'] {
		box-shadow: 0 0 16px 3px color-mix(in srgb, currentColor 60%, transparent);
	}
	[data-tick] {
		height: var(--tall);
		transition: background-color 150ms ease;
		transition-delay: calc(var(--place) * 90ms);
	}
	[data-tick][data-lit] {
		opacity: calc(0.5 + var(--place) * 0.5);
	}
	[data-settling] {
		animation: schmancy-range-settle 180ms ease-out;
	}
	@keyframes schmancy-range-settle {
		from {
			opacity: 0.35;
			translate: 0 4px;
		}
	}
	@media (prefers-reduced-motion: reduce) {
		[data-thumb],
		[data-tick] {
			transition: none;
		}
		[data-settling] {
			animation: none;
		}
	}
`),
) {
	// `formAssociated`, `internals`, `name`, `disabled`, `required`, `id`,
	// `label`, `error`, `validationMessage`, `validateOn`, touched/dirty/submitted,
	// `markTouched/markSubmitted`, `formResetCallback`, FIELD_CONNECT_EVENT
	// dispatch — all from the mixin.

	/** Theme tone shared by the rail, thumb, hover and focus treatments. */
	@property({ reflect: true }) color: SchmancyTone = 'primary'

	@property({ type: Number }) min: number = 0
	@property({ type: Number }) max: number = 1
	@property({ type: Number }) step: number = 0.01
	/** Compatible size names share the standard 48px touch target. */
	@property({ reflect: true }) size: 'default' | 'comfortable' = 'default'

	/** Numeric value — narrowed override of the mixin's wide value union. With `stops`, the place of the chosen one. */
	@property({ type: Number, reflect: true })
	override value: number = 0

	/**
	 * Named places on the rail, with words from the host: one detent each, in place of `min`, `max` and `step`.
	 * The chosen word stands above the rail, and a screen reader hears it as the value.
	 */
	@property({ attribute: false }) stops: readonly SchmancyRangeStop[] = []

	/** The id of the chosen stop. It is what a form receives, and `change` carries it as `stop`. */
	@property() stop = ''

	/** What the chosen stop means, in the host's words: under the rail, and heard after the word. */
	@property() note = ''

	/** `rising` draws a rail of ticks that climb and light up to the value, over the same native range. */
	@property({ reflect: true }) variant: 'default' | 'rising' = 'default'

	/**
	 * A native range sets its value at the press point, before the browser knows the gesture
	 * is a pan, so an open touch sequence holds the value and writes each native change straight
	 * back, ahead of the next paint. The sequence is what bounds it: a second finger gets no
	 * pointerdown while another pans, and the native slider keeps reading touches past pointercancel.
	 */
	private touchHold: 'none' | 'press' | 'moved' | 'panned' = 'none'
	private tapValue: number | null = null
	private pointerPressed = false

	override connectedCallback(): void {
		super.connectedCallback()
		merge(
			// WebKit leaves a control a pointer pressed unfocused, so the keys would go nowhere after a drag.
			fromEvent<MouseEvent>(this.renderRoot, 'click').pipe(
				map(click => click.target),
				filter((target): target is HTMLInputElement => target instanceof HTMLInputElement),
				tap((input: HTMLInputElement) => input.focus({ preventScroll: true, focusVisible: false })),
				ignoreElements(),
			),
			fromEvent<PointerEvent>(this.renderRoot, 'pointerdown').pipe(
				filter(down => down.pointerType === 'touch' && down.target instanceof HTMLInputElement),
				tap(() => {
					this.pointerPressed = true
				}),
				ignoreElements(),
			),
			fromEvent<TouchEvent>(this.renderRoot, 'touchstart', { passive: true }).pipe(
				filter(start => start.targetTouches.length === 1 && start.target instanceof HTMLInputElement),
				switchMap(start => {
					const { identifier, clientX: startX, clientY: startY } = start.targetTouches[0]
					this.touchHold = 'press'
					this.tapValue = null
					return merge(
						fromEvent<TouchEvent>(this.renderRoot, 'touchmove', { passive: true }).pipe(
							filter(() => this.touchHold === 'press' || this.touchHold === 'moved'),
							map(move => Array.from(move.targetTouches).find(touch => touch.identifier === identifier)),
							filter(
								(touch): touch is Touch =>
									touch !== undefined &&
									Math.hypot(touch.clientX - startX, touch.clientY - startY) >= POINTER_SESSION_THRESHOLD_PX,
							),
							// A tie is sideways, as the native slider reads it.
							tap((touch: Touch) => {
								this.touchHold = Math.abs(touch.clientX - startX) >= Math.abs(touch.clientY - startY) ? 'none' : 'moved'
							}),
							ignoreElements(),
						),
						fromEvent<PointerEvent>(this.renderRoot, 'pointercancel').pipe(
							filter(cancel => cancel.pointerType === 'touch'),
							tap(() => {
								this.touchHold = 'panned'
							}),
							ignoreElements(),
						),
						merge(
							fromEvent<TouchEvent>(this.renderRoot, 'touchend', { passive: true }),
							fromEvent<TouchEvent>(this.renderRoot, 'touchcancel', { passive: true }),
						).pipe(filter(end => end.targetTouches.length === 0 && end.target instanceof HTMLInputElement)),
					).pipe(
						take(1),
						finalize(() => {
							this.touchHold = 'none'
							this.pointerPressed = false
						}),
					)
				}),
				// A finger landing while another pans never receives a pointerdown: it is no tap.
				filter(end => end.type === 'touchend' && this.touchHold === 'press' && this.pointerPressed && !this.disabled),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe(() => {
				if (this.tapValue !== null) this.commitValue(this.tapValue)
			})
	}

	private commitValue(value: number): void {
		this.value = value
		this.markTouched()
		const stop = this.stops[value]?.id
		if (stop === undefined) {
			this.emitChange({ value })
			return
		}
		this.stop = stop
		// A key on a phone is not a finger on the rail.
		if (this.pointerPressed) navigator.vibrate?.(6)
		this.emitChange({ value, stop })
	}

	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (this.stops.length > 0) {
			// The host says which by `stop`; a form's reset and a bare `value` say it by place.
			const named = this.stops.findIndex(({ id }) => id === this.stop)
			if (named >= 0 && (changed.has('stop') || changed.has('stops') || !changed.has('value'))) this.value = named
			else this.stop = this.stops[Math.min(Math.max(0, this.value), this.stops.length - 1)]?.id ?? ''
		}
		if (changed.has('value') || changed.has('name') || changed.has('stop') || changed.has('stops')) {
			this.internals?.setFormValue(this.stops.length > 0 ? this.stop : String(this.value))
		}
	}

	/** FormData contributes the value as a string (native range input convention). */
	override toFormEntries(): Array<[string, FormDataEntryValue]> {
		if (!this.name || this.disabled) return []
		return [[this.name, this.stops.length > 0 ? this.stop : String(this.value)]]
	}

	render() {
		const named = this.stops.length > 0
		const rising = this.variant === 'rising'
		const min = named ? 0 : this.min
		const max = named ? this.stops.length - 1 : this.max
		const at = max > min ? (this.value - min) / (max - min) : 0
		const word = this.stops[this.value]?.word ?? ''
		const ink = toneColors(this.color, 'solid').backgroundColor
		const input = html`<input
			type="range"
			.min=${String(min)}
			.max=${String(max)}
			.step=${String(named ? 1 : this.step)}
			.value=${String(this.value)}
			?disabled=${this.disabled}
			aria-label=${this.ariaLabel || this.label || 'Range'}
			aria-valuetext=${named ? [word, this.note].filter(Boolean).join('. ') : String(this.value)}
			style=${styleMap(
				rising
					? { color: ink }
					: {
							color: ink,
							'--range-progress': `${at * 100}%`,
							'--range-label-offset-y': this.label && !named ? '-8px' : '0px',
						},
			)}
			aria-invalid=${this.error ? 'true' : 'false'}
			@input=${(e: Event) => {
				if (!(e.target instanceof HTMLInputElement)) return
				if (this.touchHold === 'none') {
					this.commitValue(Number(e.target.value))
					return
				}
				if (this.touchHold === 'press') this.tapValue = Number(e.target.value)
				e.target.value = String(this.value)
			}}
		/>`
		return html`
			<div class=${this.classMap({ 'flex min-w-0 flex-col w-full': true, 'gap-2': named })}>
				${when(
					named,
					() => html`
						<div class="flex min-h-10 min-w-0 items-center justify-between gap-2">
							<schmancy-typography data-word type="title" token="lg" class="block min-w-0 truncate"
								>${keyed(word, html`<span data-settling class="block truncate">${word}</span>`)}</schmancy-typography
							>
							<slot></slot>
						</div>
					`,
					() =>
						when(
							this.label,
							() => html`
								<div class="relative z-10 flex min-w-0 items-end justify-between gap-2">
									<schmancy-typography type="label" token="sm" class="min-w-0 flex-1 break-words">${this.label}</schmancy-typography>
									<schmancy-typography type="label" token="sm" class="shrink-0 tabular-nums opacity-60"
										>${this.value}</schmancy-typography
									>
								</div>
							`,
						),
				)}
				${when(
					rising,
					() => html`
						<div
							data-rail
							style=${styleMap({ color: ink, '--at': String(at) })}
							class=${this.classMap({ 'relative mx-1.5 h-12 touch-pan-y select-none': true, 'opacity-[0.38]': this.disabled })}
						>
							<div class="absolute inset-x-0 top-1 flex h-8 items-end justify-between" aria-hidden="true">
								${repeat(
									TICK_PLACES,
									place => place,
									place => html`<span
										data-tick
										?data-lit=${at > 0 && place <= at + 0.001}
										style=${`--place:${place};--tall:${(5 + 27 * place ** 1.35).toFixed(1)}px`}
										class=${this.classMap({ 'w-[3px] rounded-full': true, 'bg-current': at > 0 && place <= at + 0.001, 'bg-outline-variant': at === 0 || place > at + 0.001 })}
									></span>`,
								)}
							</div>
							<div class="absolute inset-x-0 bottom-0 h-1" aria-hidden="true">
								${repeat(
									this.stops,
									({ id }) => id,
									(_stop, place) => html`<span
										data-detent
										style=${`--stop:${place / Math.max(1, max)}`}
										class=${this.classMap({ 'absolute left-[calc(var(--stop)*100%)] size-1 -translate-x-1/2 rounded-full': true, 'bg-current': place <= this.value, 'bg-outline-variant': place > this.value })}
									></span>`,
								)}
							</div>
							${input}
							<span
								data-thumb
								data-glow=${at > 0.7 ? 'full' : at > 0.3 ? 'some' : 'none'}
								aria-hidden="true"
								class="absolute top-0 box-border h-10 w-3.5 -translate-x-1/2 rounded-full border-2 border-surface-container bg-current"
							></span>
						</div>
					`,
					() => input,
				)}
				${when(named, () => html`<schmancy-typography data-note type="label" token="sm" class="block min-h-4 text-surface-onVariant">${this.note}</schmancy-typography>`)}
				${
					this.error && this.validationMessage
						? html`<div role="alert" class="text-error-default text-xs mt-1">${this.validationMessage}</div>`
						: nothing
				}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-range': SchmancyRange
	}
}
